import React, { useEffect } from 'react';
import { AppProvider, Frame } from '@shopify/polaris';
import { NavMenu } from '@shopify/app-bridge-react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import PlanSettings from './pages/PlanSettings';
import Settings from './pages/Settings';
import ErrorBoundary from './components/ErrorBoundary';
import BrandLoading from './components/BrandLoading';
import TawkVisitor from './components/TawkVisitor';
import { useI18n, I18nProvider, t } from './i18n';import { polarisTranslations } from './i18n/polaris';
import {
  shopifyUrlParams, embedContextToRestore, saveEmbedContext,
  AppBridgeAuthProvider, LocalAuthProvider, InstallGate,
  ImpersonationAuthProvider, impTokenFromUrl, saveImpSession, loadImpSession, useAuth
} from './auth';
import { useApi } from './hooks/useApi';
import SupportViewBanner from './components/SupportViewBanner';
import { deploymentMismatch, API_BASE } from './constants';
import '@shopify/polaris/build/esm/styles.css';

// AppProvider 需要 Polaris 的界面词典;这里按当前 admin 语言取对应译文,
// 三种鉴权分支共用同一套接线(i18n={{}} 曾把 Polaris 内部文案锁死为英文)。
function LocalizedApp({ children }) {
  const { locale } = useI18n();
  return <AppProvider i18n={polarisTranslations(locale)}>{children}</AppProvider>;
}

// 鉴权就绪后读一次店铺设置:商家在设置页选过 admin 语言的话以服务端为准
// (店铺维度,跨浏览器一致)。本地演示模式 getSessionToken 为 null,不请求,
// 只认 localStorage 里的选择。渲染在 AppContent 内,三种鉴权分支都有
// AuthProvider 上下文。
function ServerLocaleSync() {
  const { getSessionToken } = useAuth();
  const { getSettings } = useApi();
  const { setLocale } = useI18n();
  useEffect(() => {
    if (!getSessionToken) return undefined;
    let cancelled = false;
    getSettings()
      .then((s) => {
        if (!cancelled && s && s.adminLocale) setLocale(s.adminLocale);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [getSessionToken, getSettings, setLocale]);
  return null;
}

function AppContent({ embedded }) {
  const { t } = useI18n();
  return (
    <>
      {/* 当前店铺身份上报给 Tawk 聊天,后台能看到是哪家店铺在咨询。 */}
      <TawkVisitor />
      <ServerLocaleSync />
      {embedded ? (
        /* 导航交给 App Bridge:链接会出现在 Shopify admin 原生侧边栏。
            label 取 <Link> 的文本内容,rel="home" 标记应用首页。 */
        <NavMenu>
          <Link to="/" rel="home">{t('nav.home')}</Link>
          <Link to="/plans">{t('nav.plans')}</Link>
          <Link to="/settings">{t('nav.settings')}</Link>
        </NavMenu>
      ) : (
        /* 本地开发(不在 Shopify iframe 里):页面内简单导航。 */
        <nav style={{ padding: '12px 16px', display: 'flex', gap: 16, borderBottom: '1px solid #e3e3e3' }}>
          <Link to="/">{t('nav.home')}</Link>
          <Link to="/plans">{t('nav.plans')}</Link>
          <Link to="/settings">{t('nav.settings')}</Link>
        </nav>
      )}
      {/* Frame 不带 navigation:只为 Polaris Toast 等组件提供上下文,
          不再在 iframe 里渲染侧边栏。 */}
      <Frame>
        <Routes>
          <Route path="/" element={<ErrorBoundary><Home /></ErrorBoundary>} />
          <Route path="/plans" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
          <Route path="/settings" element={<ErrorBoundary><Settings /></ErrorBoundary>} />
          {/* No separate /plans/new route: useParams().id must be 'new' for the
              new-plan form, so it has to match the :id route below. */}
          <Route path="/plans/:id" element={<ErrorBoundary><PlanSettings /></ErrorBoundary>} />
          <Route path="*" element={<ErrorBoundary><Home /></ErrorBoundary>} />
        </Routes>
      </Frame>
    </>
  );
}

// 部署绊线报错页:在鉴权建立之前就可能渲染,直接走 i18n 的同步词典
// (localStorage 有缓存则已是商家语言,否则英语)。
function DeploymentMismatchPage({ mismatch }) {
  return (
    <div style={{ maxWidth: 640, margin: '80px auto', textAlign: 'center' }}>
      <h1>{t('mismatch.title')}</h1>
      <p style={{ color: '#616161' }}>
        {t('mismatch.body', { apiBase: mismatch.apiBase, hostname: window.location.hostname, expected: mismatch.expected })}
      </p>
      <p style={{ color: '#616161' }}>
        {t('mismatch.contact')}
      </p>
    </div>
  );
}

// 三种运行模式:
//   支持模拟(URL 带 imp_token,或 sessionStorage 有支持会话)— 支持后台
//   点用户 ID 跳转而来:固定令牌直连 API,无 App Bridge,顶栏标识客服视角;
//   嵌入(Shopify admin iframe)— AppBridgeAuthProvider 在启动时完成
//   token exchange(GET /api/auth),之后所有请求带 session token;
//   本地(localhost:3000)— LocalAuthProvider 保持原有 demo 店铺行为。
export default function App() {
  // 部署绊线必须最先做:域名与构建打进的 API 环境不匹配时拒绝渲染,
  // 宁可明确报错也不把请求泄给另一个环境(背景见 constants.js 的绊线注释)。
  const mismatch = deploymentMismatch(window.location.hostname, API_BASE);
  if (mismatch) {
    return <I18nProvider><DeploymentMismatchPage mismatch={mismatch} /></I18nProvider>;
  }

  // 支持会话判定必须最先做:URL 上的 imp_token 进 sessionStorage 后立刻
  // 洗掉(截图/分享地址不会泄露令牌),且不能落入下方嵌入上下文恢复逻辑。
  const freshImp = impTokenFromUrl();
  if (freshImp) {
    saveImpSession(freshImp.token, freshImp.shop);
    const params = new URLSearchParams(window.location.search);
    params.delete('imp_token');
    const qs = params.toString();
    window.history.replaceState(
      null, '',
      window.location.pathname + (qs ? '?' + qs : '') + window.location.hash
    );
  }
  const impSession = loadImpSession();
  if (impSession) {
    return (
      <I18nProvider>
        <LocalizedApp>
          <BrowserRouter>
            <ImpersonationAuthProvider session={impSession}>
              <SupportViewBanner />
              <AppContent />
            </ImpersonationAuthProvider>
          </BrowserRouter>
        </LocalizedApp>
      </I18nProvider>
    );
  }

  const { embedded, shop, host } = shopifyUrlParams();
  if (embedded) {
    saveEmbedContext(shop, host);
  } else {
    // App Bridge 的整页跳转可能把 iframe 重载到不带 ?shop=&host= 的地址;
    // 补全参数并重载一次,避免应用误入本地模式后发出无令牌的请求。
    const saved = embedContextToRestore();
    if (saved) {
      const params = new URLSearchParams(window.location.search);
      params.set('shop', saved.shop);
      params.set('host', saved.host);
      window.location.replace(
        window.location.pathname + '?' + params.toString() + window.location.hash
      );
      // 重定向进行中不渲染任何页面(否则会在无令牌状态下发出一批 400)
      return (
        <I18nProvider>
          <LocalizedApp>
            <BrandLoading />
          </LocalizedApp>
        </I18nProvider>
      );
    }
  }
  return (
    <I18nProvider>
      <BrowserRouter>
        {embedded ? (
          <LocalizedApp>
            <AppBridgeAuthProvider>
              <InstallGate>
                <AppContent embedded />
              </InstallGate>
            </AppBridgeAuthProvider>
          </LocalizedApp>
        ) : (
          <LocalizedApp>
            <LocalAuthProvider>
              <AppContent />
            </LocalAuthProvider>
          </LocalizedApp>
        )}
      </BrowserRouter>
    </I18nProvider>
  );
}
